"use client";

import { motion, useScroll } from "motion/react";
import React, { useRef } from "react";
import VelocityMarqueePrimary from "./VelocityMarqueePrimary";
import Image from "next/image";
import blessed from "../../assets/icons/blessed-face.svg";
import happy from "../../assets/icons/happy-face.svg";
import smirk from "../../assets/icons/smirk-face.svg";
import surprised from "../../assets/icons/surprised-face.svg";
import wrigglyUp from "../../assets/icons/wrrigly-up.svg";
import wrigglyDown from "../../assets/icons/wriggly-down.svg";
import MarketingFlashcard from "./MarketingFlashcard";
import MaxWidthWrapper from "../shared/MaxWidthWrapper";
import SaveEmailInput from "./SaveEmailInput";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useResponsiveAnimations } from "../../hooks/animations/useResponsiveAnimations";
import {
  ANIMATION_CONFIG,
  FLASHCARD_TEXTS,
  FLASHCARD_COLORS,
} from "../../constants/animations";

const MarketingSection = () => {
  const marketingRef = useRef(null);
  const { width: viewportWidth } = useWindowSize();

  const { scrollYProgress } = useScroll({
    target: marketingRef,
    offset: ["start end", "end end"],
  });

  // Create animations for flashcards
  const cardAnimations = {
    card1: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.flashcards.card1
    ),
    card2: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.flashcards.card2
    ),
    card3: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.flashcards.card3
    ),
    card4: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.flashcards.card4
    ),
  };

  // Create animations for emoticons
  const emoticonAnimations = {
    blessed: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.emoticons.blessed
    ),
    happy: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.emoticons.happy
    ),
    smirk: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.emoticons.smirk
    ),
    surprised: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.emoticons.surprised
    ),
    wrigglyUp: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.emoticons.wrigglyUp
    ),
    wrigglyDown: useResponsiveAnimations(
      scrollYProgress,
      viewportWidth,
      ANIMATION_CONFIG.emoticons.wrigglyDown
    ),
  };

  return (
    <>
      <motion.div
        className="bg-white min-h-[1100px] w-full relative z-10 overflow-x-hidden"
        id="marketing-section"
      >
        <VelocityMarqueePrimary />
        <MaxWidthWrapper>
          <main
            className="w-full flex sm:flex-row flex-col sm:items-center justify-center sm:min-h-[900px] relative max-sm:mt-8 max-sm:gap-4 3xl:h-[calc(100vh-200px)]"
            ref={marketingRef}
          >
            {/* Center Content */}
            <div className="flex flex-col items-center relative z-20 3xl:max-w-[80%]">
              <div className="flex flex-col items-center justify-center gap-y-9 3xl:gap-y-16">
                <div className="text-lg sm:text-[21px] bg-[#E8F0F9] rounded-[20px] py-2 px-6 max-sm:text-center">
                  Give your learning a{" "}
                  <span className="font-bold">glow up*</span>
                </div>
                <h1 className="text-2xl sm:text-[54px] lg:text-[68px] 3xl:text-[3.5vw] font-editorial font-bold tracking-[-0.05em] text-center max-w-[90vw] 3xl:w-[1600px]">
                  Let&apos;s Get the Party Started
                </h1>
              </div>
              <div className="mt-12 3xl:mt-12 max-sm:w-full">
                <SaveEmailInput />
              </div>
            </div>

            {/* Animated Elements */}
            {/* Flashcards */}
            {Object.entries(cardAnimations).map(([key, animation], index) => (
              <motion.div
                key={key}
                className="max-sm:hidden absolute z-10"
                style={animation}
              >
                <MarketingFlashcard
                  text={FLASHCARD_TEXTS[index]}
                  color={FLASHCARD_COLORS[index]}
                />
              </motion.div>
            ))}

            {/* Emoticons */}
            <motion.div
              className="max-sm:hidden absolute z-10"
              style={emoticonAnimations.blessed}
            >
              <Image src={blessed} alt="blessed" />
            </motion.div>
            <motion.div
              className="max-sm:hidden absolute z-10"
              style={emoticonAnimations.happy}
            >
              <Image src={happy} alt="happy" />
            </motion.div>
            <motion.div
              className="max-sm:hidden absolute z-10"
              style={emoticonAnimations.smirk}
            >
              <Image src={smirk} alt="smirk" />
            </motion.div>
            <motion.div
              className="max-sm:hidden absolute z-10"
              style={emoticonAnimations.surprised}
            >
              <Image src={surprised} alt="surprised" />
            </motion.div>
            <motion.div
              className="max-sm:hidden absolute z-10"
              style={emoticonAnimations.wrigglyUp}
            >
              <Image src={wrigglyUp} alt="wriggly up" />
            </motion.div>
            <motion.div
              className="max-sm:hidden absolute z-10"
              style={emoticonAnimations.wrigglyDown}
            >
              <Image src={wrigglyDown} alt="wriggly down" />
            </motion.div>

            {/* Mobile Flashcard Stack */}
            <div className="sm:hidden flex flex-col items-center gap-6 justify-center z-10 mt-6 overflow-hidden">
              {[...Array(4)].map((_, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    ease: "easeInOut",
                    delay: index * 0.1,
                  }}
                >
                  <MarketingFlashcard
                    text={FLASHCARD_TEXTS[index]}
                    color={FLASHCARD_COLORS[index]}
                  />
                </motion.div>
              ))}
            </div>
          </main>
        </MaxWidthWrapper>
      </motion.div>
    </>
  );
};

export default MarketingSection;
